import { RefreshCw, Clock, AlertCircle, Info } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useIntegrationsList } from '@/lib/api/integrations'
import { useSyncShopifyProducts, useSyncLimitsStatus, useWoocommerceSyncProducts } from '@/lib/api/products'
import { useToast } from '@/components/ui/use-toast'
import { useTaskPolling } from '@/hooks/useTaskPolling'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'

import { UnsupportedPluginModal } from './unsupported-plugin-modal'
import { extractInvalidEnumTypes, parseInvalidEnumTypesFromMessage } from './unsupported-plugin'

interface ShopifySyncProductButtonProps {
    storeId: string
    onSyncComplete?: () => void
}

/**
 * Button component to sync products from Shopify
 * Only displays if a Shopify integration is enabled for the store
 */
export const ShopifySyncProductButton = ({ storeId, onSyncComplete }: ShopifySyncProductButtonProps) => {
    const { t } = useTranslation(['stores'])
    const { toast } = useToast()
    const { data: integrations } = useIntegrationsList(storeId)
    const { data: syncLimits } = useSyncLimitsStatus(storeId)
    const syncMutation = useSyncShopifyProducts()
    const woocommerceSyncMutation = useWoocommerceSyncProducts()
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [showPopover, setShowPopover] = useState(false)
    const [unsupportedTypes, setUnsupportedTypes] = useState<string[] | null>(null)

    const hasShopifyIntegration = integrations?.some(
        (integration) => (integration.provider === 'shopify' || integration.provider === 'woocommerce') && integration.enabled
    )

    const integrationLabel = integrations?.find((i) => i.provider === 'woocommerce') ? 'Woocommerce' : 'Shopify'

    // Poll for task status
    const { isPolling } = useTaskPolling({
        taskId: currentTaskId,
        onCompleted: (output) => {
            const errors = output?.stats?.productErrors ?? []
            const synced = output?.stats?.productsFetched ?? 0
            if (errors.length > 0) {
                toast({
                    title: t('stores:productSync.syncCompletedWithErrorsTitle'),
                    description: t('stores:productSync.syncCompletedWithErrorsDescription', {
                        synced,
                        errorCount: errors.length,
                    }),
                    variant: 'destructive',
                })
                const unsupported = extractInvalidEnumTypes(errors)
                if (unsupported.length > 0) setUnsupportedTypes(unsupported)
            } else {
                toast({
                    title: t('stores:productSync.syncCompletedTitle'),
                    description: t('stores:productSync.syncCompletedDescription', { synced }),
                })
            }
            setCurrentTaskId(null)
            onSyncComplete?.()
        },
        onFailed: (error) => {
            toast({
                title: t('stores:productSync.syncFailedTitle'),
                description: error,
                variant: 'destructive',
            })
            const unsupported = parseInvalidEnumTypesFromMessage(error)
            if (unsupported.length > 0) setUnsupportedTypes(unsupported)
            setCurrentTaskId(null)
        },
    })

    const handleSyncClick = () => {
        setShowConfirmDialog(true)
    }

    const handleMutationSuccess = (data: { message: string; taskId?: string; remainingSyncs?: number }) => {
        const remainingText =
            data.remainingSyncs !== undefined ? ` You have ${data.remainingSyncs} sync(s) remaining today.` : ''
        toast({
            title: t('stores:productSync.syncStartedTitle'),
            description: t('stores:productSync.syncStartedDescription', {
                message: data.message,
                remainingText,
            }),
        })
        if (data.taskId) {
            setCurrentTaskId(data.taskId)
        }
    }

    const handleMutationError = (error: { message: string }) => {
        toast({
            title: t('stores:productSync.syncFailedTitle'),
            description: error.message,
            variant: 'destructive',
        })
    }

    const handleSyncWooCommerce = () => {
        woocommerceSyncMutation.mutate(
            { storeId },
            {
                onSuccess: handleMutationSuccess,
                onError: handleMutationError,
            }
        )
    }

    const handleConfirmSync = () => {
        setShowConfirmDialog(false)

        if (isPolling || syncMutation.isPending) return

        if (integrationLabel === 'Woocommerce') {
            handleSyncWooCommerce()
            return
        }

        syncMutation.mutate(
            { storeId },
            {
                onSuccess: handleMutationSuccess,
                onError: handleMutationError,
            }
        )
    }

    if (!hasShopifyIntegration) {
        return null
    }

    const isLoading = syncMutation.isPending || isPolling
    const canSync = syncLimits?.canSync ?? true
    const isDisabled = isLoading || !canSync

    return (
        <>
            <Popover open={showPopover} onOpenChange={setShowPopover}>
                <PopoverTrigger asChild>
                    <div
                        onMouseEnter={() => setShowPopover(true)}
                        onMouseLeave={() => setShowPopover(false)}
                    >
                        <Button
                            onClick={handleSyncClick}
                            disabled={isDisabled}
                            variant="outline"
                            size="sm"
                        >
                            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                            {isLoading ? 'Syncing...' : `Sync from ${integrationLabel}`}
                        </Button>
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    className="w-80"
                    side="bottom"
                    align="end"
                    onMouseEnter={() => setShowPopover(true)}
                    onMouseLeave={() => setShowPopover(false)}
                >
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Info className="h-4 w-4 text-blue-500" />
                            <h4 className="font-semibold text-sm">Sync Limits</h4>
                        </div>
                        {syncLimits ? (
                            <div className="space-y-1 text-sm text-muted-foreground">
                                <p>
                                    <span className="font-medium text-foreground">
                                        {syncLimits.remainingSyncs}/{syncLimits.maxSyncsPerDay}
                                    </span> syncs remaining today
                                </p>
                                <p className="text-xs">
                                    • Maximum {syncLimits.maxSyncsPerDay} syncs per day
                                </p>
                                <p className="text-xs">
                                    • Minimum {syncLimits.minIntervalMinutes} minutes between syncs
                                </p>
                                {!canSync && syncLimits.reason && (
                                    <p className="text-xs text-destructive mt-2 pt-2 border-t">
                                        {syncLimits.reason}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Loading limits...</p>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-orange-500" />
                            Confirm Product Sync
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3 text-left">
                            <p className="font-medium text-foreground">
                                This is a heavy process that will sync all products and collections from {integrationLabel}.
                            </p>

                            <div className="rounded-md bg-orange-50 border border-orange-200 p-3">
                                <p className="text-sm text-orange-900">
                                    <strong>⏱️ Estimated duration:</strong> 1 to 45 minutes depending on the number of products and collections.
                                </p>
                            </div>

                            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 space-y-2">
                                <p className="text-sm text-blue-900 font-medium">
                                    Rate Limits:
                                </p>
                                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                                    <li>Maximum {syncLimits?.maxSyncsPerDay || 7} syncs per day</li>
                                    <li>Minimum {syncLimits?.minIntervalMinutes || 10} minutes between syncs</li>
                                    {syncLimits && (
                                        <li className="font-semibold">
                                            You have {syncLimits.remainingSyncs} sync{syncLimits.remainingSyncs !== 1 ? 's' : ''} remaining today
                                        </li>
                                    )}
                                </ul>
                            </div>

                            <div className="rounded-md bg-green-50 border border-green-200 p-3">
                                <p className="text-sm text-green-900">
                                    <AlertCircle className="inline h-4 w-4 mr-1" />
                                    <strong>No downtime:</strong> During the sync process, your checkout will continue to work normally with existing product data.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmSync}>
                            Start Sync
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <UnsupportedPluginModal
                open={unsupportedTypes !== null}
                onOpenChange={(open) => {
                    if (!open) setUnsupportedTypes(null)
                }}
                types={unsupportedTypes ?? []}
            />
        </>
    )
}
